import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { WEEKDAY, DayType } from '../utils/week';
import Modal from './Modal';
import { useSchedules } from '../context/ScheduleContext';
import type { Schedule } from '../types/schedule';

type ThisMonthDateType = {
  year: number;
  month: number;
  date: number;
};

export default function ThisMonthDate({ year, month, date }: ThisMonthDateType) {
  const [isOpen, setIsOpen] = useState(false);
  const [day, setDay] = useState<DayType>('' as DayType);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const { getSchedulesByDate } = useSchedules();
  const [dateSchedules, setDateSchedules] = useState<Schedule[]>([]);

  const formatDate = (year: number, month: number, date: number): string => {
    return `${year}-${month.toString().padStart(2, '0')}-${date.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const formattedDate = formatDate(year, month, date);
    const schedules = getSchedulesByDate(formattedDate);
    setDateSchedules(schedules);
  }, [year, month, date, getSchedulesByDate]);

  const getThisDay = (year: number, month: number, date: number) => {
    // month는 1부터 시작하므로 0부터 시작하는 JavaScript Date 객체에 맞게 조정
    const DATE: Date = new Date(year, month - 1, date);
    const DAY = DATE.getDay();
    setDay(WEEKDAY[DAY] as DayType);
  };

  const toggleModal = (year: number, month: number, date: number) => {
    if (isOpen) {
      setIsOpen(false);
      setSelectedSchedule(undefined);
      setIsEdit(false);
    } else {
      getThisDay(year, month, date);
      setIsOpen(true);
    }
  };

  const handleScheduleClick = (e: React.MouseEvent, schedule: Schedule) => {
    e.stopPropagation();
    getThisDay(year, month, date);
    setSelectedSchedule(schedule);
    setIsEdit(true);
    setIsOpen(true);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '교역자':
        return '#3d85f5';
      case '목자':
        return '#0fa136';
      case '기타':
        return '#f59f38';
      default:
        return '#888888';
    }
  };

  // 일정 표시 텍스트 생성 함수
  const getScheduleDisplayText = (schedule: Schedule): string => {
    const currentDate = formatDate(year, month, date);

    // 기간 설정된 일정인 경우
    if (schedule.end_date) {
      // 시작일인 경우
      if (schedule.date === currentDate) {
        return `[시작] ${schedule.content}`;
      }
      // 종료일인 경우
      else if (schedule.end_date === currentDate) {
        return `[종료] ${schedule.content}`;
      }
      // 중간 날짜인 경우
      else {
        return `[진행중] ${schedule.content}`;
      }
    }

    // 단일 날짜 일정
    return schedule.content;
  };

  return (
    <>
      {isOpen && (
        <Modal
          year={year}
          month={month}
          date={date}
          day={day}
          toggleModal={toggleModal}
          selectedSchedule={selectedSchedule}
          isEdit={isEdit}
        />
      )}
      <DateWrapper onClick={() => toggleModal(year, month, date)}>
        <DateNumber>{date}</DateNumber>
        <Schedules>
          {dateSchedules.map((schedule) => (
            <ScheduleItem
              key={schedule.id}
              color={getCategoryColor(schedule.category)}
              onClick={(e) => handleScheduleClick(e, schedule)}
              isDateRange={!!schedule.end_date}
            >
              {getScheduleDisplayText(schedule)}
            </ScheduleItem>
          ))}
        </Schedules>
      </DateWrapper>
    </>
  );
}

const DateWrapper = styled.div`
  height: 180px;
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  overflow-y: auto;

  &:nth-child(7n + 1) {
    color: ${({ theme }) => theme.sundayColor};
    background-color: #fafdce;
  }

  &:nth-child(7n) {
    background-color: #a0c0f05c;
  }

  &:not(:nth-child(7n)) {
    border-right: 1px solid ${({ theme }) => theme.borderColor};
  }

  &:hover {
    cursor: pointer;
    filter: brightness(0.95);
  }
`;

const DateNumber = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const Schedules = styled.ul`
  padding: 0;
`;

const ScheduleItem = styled.li<{ color: string; isDateRange?: boolean }>`
  margin-bottom: 5px;
  padding: 5px;
  border-radius: 4px;
  background-color: ${(props) => props.color};
  font-size: 0.8rem;
  line-height: 1.3;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  ${(props) =>
    props.isDateRange &&
    `
    border-left: 4px solid #fff;
  `}

  &:hover {
    filter: brightness(1.1);
  }
`;
